import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../src/Config";
import { useContext } from "react";
import { ThemeContext } from "../../src/ThemeContext";
import DatePicker from "react-datepicker";
import { FiUpload, FiDownload, FiEye } from "react-icons/fi";
import { MdAttachFile } from "react-icons/md";
import { IoCloseOutline } from "react-icons/io5";
import Select from "react-select";
import { Chip } from "@mui/material";

function EditVoyage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { ID } = useParams();
  const { theme } = useContext(ThemeContext);
  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const isEditMode = Boolean(ID); // ✅ True if editing, false if adding new
  // State management
  const [errors, setErrors] = useState({});
  const [severity, setSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);

  const [addedTags, setAddedTags] = useState([]);
  const [addedTagsMS, setAddedTagsMS] = useState([]);
  const [addedTagsHSD, setAddedTagsHSD] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagOptionsMS, setTagOptionsMS] = useState([]);
  const [tagOptionsHSD, setTagOptionsHSD] = useState([]);
  const [selectedTagNo, setSelectedTagNo] = useState(null);
  const [selectedTagNoMS, setSelectedTagNoMS] = useState(null);
  const [selectedTagNoHSD, setSelectedTagNoHSD] = useState(null);
  // const [dynamicRefs, setDynamicRefs] = useState({});

  // Form data state
  const [voyageData, setVoyageData] = useState({
    voyage_number: "",
    date: null,
    trip_number: "",
  });

  // Fuel selection state
  const [selectedFuels, setSelectedFuels] = useState({
    ATF: false,
    MS: false,
    HSD: false,
  });

  // Document upload states
  const [uploadStateMS, setUploadStateMS] = useState({});
  const [uploadStateHSD, setUploadStateHSD] = useState({});
  const [uploadStateTravel, setUploadStateTravel] = useState({});
  const [uploadStateTanker, setUploadStateTanker] = useState({});

  const documentTypes = {
    atf_load_port_doc: {
      label: "Load Port Test Report",
      key: "loadPortTest",
    },
    atf_pd_test: {
      label: "PD Test",
      key: "pdTest",
    },
    bft_report: {
      label: "BFT Report",
      key: "bftReport",
    },
    av2_report: {
      label: "AV2 Reports",
      key: "av2Reports",
    },
    test_h: {
      label: "Test H",
      key: "testH",
    },
  };
  const documentUploadMap = {
    ms: {
      ms_load_port_test: {
        label: "Load Port Test Report",
        key: "msLoadPortTestReport",
      },
      ms_pd_test: { label: "PD Test", key: "msPdTest" },
      ms_bft_report: { label: "BFT Report", key: "msBftReport" },
    },
    hsd: {
      hsd_load_port_test: {
        label: "Load Port Test Report",
        key: "hsdLoadPortTestReport",
      },
      hsd_pd_test: { label: "PD Test", key: "hsdPdTest" },
      hsd_bft_report: { label: "BFT Report", key: "hsdBftReport" },
    },
  };

  const documentUploadMapCommon = {
    travel: {
      tanker_load_port_doc: {
        label: "Travel Load Port Doc",
        key: "travelLoadPortDoc",
      },
    },
    tanker: {
      tanker_docs: {
        label: "Tanker Documentation",
        key: "tankerDocumentation",
      },
    },
  };

  // File input refs
  const tankerLoadPortRef = useRef(null);
  const loadPortTestRef = useRef(null);
  const pdTestRef = useRef(null);
  const bftReportRef = useRef(null);
  const tankerDocsRef = useRef(null);
  const testHRef = useRef(null);
  const av2ReportsRef = useRef(null);

  const fileInputRefs = {
    tankerLoadPort: tankerLoadPortRef,
    loadPortTest: loadPortTestRef, // This should be for atf_load_port_doc
    pdTest: pdTestRef,
    bftReport: bftReportRef,
    av2Reports: av2ReportsRef, // Add this line
    tankerDocs: tankerDocsRef,
    testH: testHRef,
  };

  const fileInputRefsDynamic = useRef({});

  // ✅ Helper to safely create a dynamic ref if missing
  const createDynamicRef = (key) => {
    if (!fileInputRefsDynamic.current[key]) {
      fileInputRefsDynamic.current[key] = React.createRef();
    }
    return fileInputRefsDynamic.current[key];
  };

  const msStaticRefs = {
    msLoadPortTestReport: useRef(null),
    msPdTest: useRef(null),
    msBftReport: useRef(null),
  };

  const hsdStaticRefs = {
    hsdLoadPortTestReport: useRef(null),
    hsdPdTest: useRef(null),
    hsdBftReport: useRef(null),
  };

  const dynamicRefs = useRef({});

  const travelRefs = {
    travelLoadPortDoc: useRef(null),
  };

  const tankerRefs = {
    tankerDocumentation: useRef(null),
  };

  // Initialize document states
  const initializeDocumentStates = () => {
    const states = {};
    Object.keys(documentTypes).forEach((docType) => {
      const key = documentTypes[docType].key;
      states[`selected${key.charAt(0).toUpperCase() + key.slice(1)}Files`] = [];
      states[`uploaded${key.charAt(0).toUpperCase() + key.slice(1)}Files`] = [];
      states[
        `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`
      ] = false;
    });
    return states;
  };

  const [documentStates, setDocumentStates] = useState(
    initializeDocumentStates()
  );

  // Utility functions
  const showSnackbar = (message, severityType = "success") => {
    setSnackbarMessage(message);
    setSeverity(severityType);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveClick();
    }
  };

  // Check if any documents are selected for upload
  const hasSelectedDocuments = () => {
    // Check ATF documents
    const hasATFDocs = Object.keys(documentTypes).some((docType) => {
      const key = documentTypes[docType].key;
      const selectedKey = `selected${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      return documentStates[selectedKey]?.length > 0;
    });

    // Check MS documents
    const hasMSDocs = Object.keys(documentUploadMap.ms).some((docType) => {
      const key = documentUploadMap.ms[docType].key;
      const selectedKey = `selected${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      return uploadStateMS[selectedKey]?.length > 0;
    });

    // Check HSD documents
    const hasHSDDocs = Object.keys(documentUploadMap.hsd).some((docType) => {
      const key = documentUploadMap.hsd[docType].key;
      const selectedKey = `selected${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      return uploadStateHSD[selectedKey]?.length > 0;
    });

    // Check Travel documents
    const hasTravelDocs = Object.keys(documentUploadMapCommon.travel).some(
      (docType) => {
        const key = documentUploadMapCommon.travel[docType].key;
        const selectedKey = `selected${
          key.charAt(0).toUpperCase() + key.slice(1)
        }Files`;
        return uploadStateTravel[selectedKey]?.length > 0;
      }
    );

    // Check Tanker documents
    const hasTankerDocs = Object.keys(documentUploadMapCommon.tanker).some(
      (docType) => {
        const key = documentUploadMapCommon.tanker[docType].key;
        const selectedKey = `selected${
          key.charAt(0).toUpperCase() + key.slice(1)
        }Files`;
        return uploadStateTanker[selectedKey]?.length > 0;
      }
    );

    return (
      hasATFDocs || hasMSDocs || hasHSDDocs || hasTravelDocs || hasTankerDocs
    );
  };

  // Prepare document FormData for upload
  // ---old ---
  // const prepareDocumentFormData = () => {
  //   const formData = new FormData();

  //   // Add voyage_id
  //   formData.append("voyage_id", ID);

  //   // Add selected fuels
  //   const selectedFuelList = Object.entries(selectedFuels)
  //     .filter(([_, isChecked]) => isChecked)
  //     .map(([fuel]) => fuel);
  //   formData.append("selectedFuels", JSON.stringify(selectedFuelList));

  //   const MAX_FILE_SIZE_MB = 10;
  //   const isFileSizeValid = (file) =>
  //     file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

  //   // ATF documents
  //   for (const docType of Object.keys(documentTypes)) {
  //     const selectedFiles = getSelectedFiles(docType);
  //     for (const fileDetail of selectedFiles) {
  //       const file = fileDetail.file;
  //       if (file) {
  //         if (!isFileSizeValid(file)) {
  //           setSeverity("error");
  //           showSnackbar(
  //             `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
  //           );
  //           return null; // 🛑 Stop everything
  //         }
  //         formData.append(docType, file);
  //       }
  //     }
  //   }

  //   // MS and HSD documents
  //   for (const [product, docs] of Object.entries(documentUploadMap)) {
  //     const state = product === "ms" ? uploadStateMS : uploadStateHSD;

  //     for (const docType of Object.keys(docs)) {
  //       const key = docs[docType].key;
  //       const selectedKey = `selected${
  //         key.charAt(0).toUpperCase() + key.slice(1)
  //       }Files`;

  //       const selectedFiles = state[selectedKey] || [];
  //       for (const fileDetail of selectedFiles) {
  //         const file = fileDetail.file;
  //         if (file) {
  //           if (!isFileSizeValid(file)) {
  //             setSeverity("error");
  //             showSnackbar(
  //               `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
  //             );
  //             return null;
  //           }
  //           formData.append(docType, file);
  //         }
  //       }
  //     }
  //   }

  //   // Common documents (Travel & Tanker)
  //   for (const [category, docs] of Object.entries(documentUploadMapCommon)) {
  //     const state =
  //       category === "travel"
  //         ? uploadStateTravel
  //         : category === "tanker"
  //         ? uploadStateTanker
  //         : null;

  //     if (!state) continue;

  //     for (const docType of Object.keys(docs)) {
  //       const key = docs[docType].key;
  //       const selectedKey = `selected${
  //         key.charAt(0).toUpperCase() + key.slice(1)
  //       }Files`;

  //       const selectedFiles = state[selectedKey] || [];
  //       for (const fileDetail of selectedFiles) {
  //         const file = fileDetail.file;
  //         if (file) {
  //           if (!isFileSizeValid(file)) {
  //             setSeverity("error");
  //             showSnackbar(
  //               `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
  //             );
  //             return null;
  //           }
  //           formData.append(docType, file);
  //         }
  //       }
  //     }
  //   }

  //   return formData;
  // };

  const getSelectedFilesMS = (docType, dynamicConfig = null) => {
    if (dynamicConfig) {
      // For dynamic BFT reports
      const dynamicKey = dynamicConfig.key;
      const selectedKey = `selected${
        dynamicKey.charAt(0).toUpperCase() + dynamicKey.slice(1)
      }Files`;
      return uploadStateMS[selectedKey] || [];
    } else {
      // For static documents
      const config = documentUploadMap.ms[docType];
      if (!config) return [];
      const selectedKey = `selected${
        config.key.charAt(0).toUpperCase() + config.key.slice(1)
      }Files`;
      return uploadStateMS[selectedKey] || [];
    }
  };

  const getSelectedFilesHSD = (docType, dynamicConfig = null) => {
    if (dynamicConfig) {
      // For dynamic BFT reports
      const dynamicKey = dynamicConfig.key;
      const selectedKey = `selected${
        dynamicKey.charAt(0).toUpperCase() + dynamicKey.slice(1)
      }Files`;
      return uploadStateHSD[selectedKey] || [];
    } else {
      // For static documents
      const config = documentUploadMap.hsd[docType];
      if (!config) return [];
      const selectedKey = `selected${
        config.key.charAt(0).toUpperCase() + config.key.slice(1)
      }Files`;
      return uploadStateHSD[selectedKey] || [];
    }
  };

  // ---new ---
  const prepareDocumentFormData = () => {
    console.log("hheellooo");
    const formData = new FormData();

    // ✅ Add voyage_id
    formData.append("voyage_id", ID);

    // const selectedProducts = Object.keys(selectedFuels).filter(
    //   (fuel) => selectedFuels[fuel]
    // );
    // selectedProducts.forEach((product) => {
    //   formData.append("selectedFuels", product); // ✅ multiple keys
    // });

    const MAX_FILE_SIZE_MB = 10;
    const isFileSizeValid = (file) =>
      file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

    // ✅ Make sure these helpers are declared here:

    // ✅ Static ATF documents (excluding bft_report)
    for (const docType of Object.keys(documentTypes)) {
      if (docType === "bft_report") continue;

      const selectedFiles = getSelectedFiles(docType);
      for (const fileDetail of selectedFiles) {
        const file = fileDetail.file;
        if (file) {
          if (!isFileSizeValid(file)) {
            setSeverity("error");
            showSnackbar(
              `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
            );
            return null; // 🛑 Stop everything
          }
          formData.append(docType, file);
        }
      }
    }

    // ✅ Dynamic ATF BFT Report files
    (addedTags || []).forEach((tag) => {
      const tankIdentifier = tag.tag_no || tag.id;
      const tankDocType = `atf_bft_report_${tankIdentifier}`;
      const dynamicKey = `bftReport_${tankIdentifier}`;
      const config = {
        key: dynamicKey,
        label: `ATF BFT Report - ${tag.tagNo}`,
      };
      const selectedFiles = getSelectedFiles(tankDocType, config);
      for (const fileDetail of selectedFiles) {
        const file = fileDetail.file;
        if (file) {
          if (!isFileSizeValid(file)) {
            setSeverity("error");
            showSnackbar(
              `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
            );
            return null;
          }
          formData.append(tankDocType, file);
        }
      }
    });

    // ✅ Static MS & HSD documents (excluding bft_report)
    for (const [product, docs] of Object.entries(documentUploadMap)) {
      const state = product === "ms" ? uploadStateMS : uploadStateHSD;

      for (const docType of Object.keys(docs)) {
        if (docType.includes("bft_report")) continue;

        const key = docs[docType].key;
        const selectedKey = `selected${
          key.charAt(0).toUpperCase() + key.slice(1)
        }Files`;

        const selectedFiles = state[selectedKey] || [];
        for (const fileDetail of selectedFiles) {
          const file = fileDetail.file;
          if (file) {
            if (!isFileSizeValid(file)) {
              setSeverity("error");
              showSnackbar(
                `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
              );
              return null;
            }
            formData.append(docType, file);
          }
        }
      }
    }

    // ✅ Dynamic MS BFT Reports
    (addedTagsMS || []).forEach((tag) => {
      const tankIdentifier = tag.tag_no || tag.tagNo || tag.id;
      const tankDocType = `ms_bft_report_${tankIdentifier}`;
      const dynamicKey = `msBftReport_${tankIdentifier}`;
      const config = {
        key: dynamicKey,
        label: `MS BFT Report - ${tag.tagNo}`,
      };
      const selectedFiles = getSelectedFilesMS(tankDocType, config);
      for (const fileDetail of selectedFiles) {
        const file = fileDetail.file;
        if (file) {
          if (!isFileSizeValid(file)) {
            setSeverity("error");
            showSnackbar(
              `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
            );
            return null;
          }
          formData.append(tankDocType, file);
        }
      }
    });

    // ✅ Dynamic HSD BFT Reports
    (addedTagsHSD || []).forEach((tag) => {
      const tankIdentifier = tag.tag_no || tag.tagNo || tag.id;
      const tankDocType = `hsd_bft_report_${tankIdentifier}`;
      const dynamicKey = `hsdBftReport_${tankIdentifier}`;
      const config = {
        key: dynamicKey,
        label: `HSD BFT Report - ${tag.tagNo}`,
      };
      const selectedFiles = getSelectedFilesHSD(tankDocType, config);
      for (const fileDetail of selectedFiles) {
        const file = fileDetail.file;
        if (file) {
          if (!isFileSizeValid(file)) {
            setSeverity("error");
            showSnackbar(
              `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
            );
            return null;
          }
          formData.append(tankDocType, file);
        }
      }
    });

    // ✅ Common documents (Travel & Tanker)
    for (const [category, docs] of Object.entries(documentUploadMapCommon)) {
      const state =
        category === "travel"
          ? uploadStateTravel
          : category === "tanker"
          ? uploadStateTanker
          : null;

      if (!state) continue;

      for (const docType of Object.keys(docs)) {
        const key = docs[docType].key;
        const selectedKey = `selected${
          key.charAt(0).toUpperCase() + key.slice(1)
        }Files`;

        const selectedFiles = state[selectedKey] || [];
        for (const fileDetail of selectedFiles) {
          const file = fileDetail.file;
          if (file) {
            if (!isFileSizeValid(file)) {
              setSeverity("error");
              showSnackbar(
                `File "${file.name}" must be 10 MB or less. Please upload a smaller file.`
              );
              return null;
            }
            formData.append(docType, file);
          }
        }
      }
    }

    return formData;
  };

  // const prepareDocumentFormData = () => {
  //   const formData = new FormData();

  //   // Add voyage_id
  //   formData.append("voyage_id", ID);

  //   // Add selected fuels
  //   const selectedFuelList = Object.entries(selectedFuels)
  //     .filter(([_, isChecked]) => isChecked)
  //     .map(([fuel]) => fuel);
  //   formData.append("selectedFuels", JSON.stringify(selectedFuelList));

  //   // ATF documents
  //   Object.keys(documentTypes).forEach((docType) => {
  //     const selectedFiles = getSelectedFiles(docType);
  //     selectedFiles.forEach((fileDetail) => {
  //       if (fileDetail.file) {
  //         formData.append(docType, fileDetail.file);
  //       }
  //     });
  //   });

  //   // MS and HSD documents
  //   Object.entries(documentUploadMap).forEach(([product, docs]) => {
  //     const state = product === "ms" ? uploadStateMS : uploadStateHSD;

  //     Object.keys(docs).forEach((docType) => {
  //       const key = docs[docType].key;
  //       const selectedKey = `selected${
  //         key.charAt(0).toUpperCase() + key.slice(1)
  //       }Files`;

  //       const selectedFiles = state[selectedKey] || [];
  //       selectedFiles.forEach((fileDetail) => {
  //         if (fileDetail.file) {
  //           formData.append(docType, fileDetail.file);
  //         }
  //       });
  //     });
  //   });

  //   // Common documents (Travel & Tanker)
  //   Object.entries(documentUploadMapCommon).forEach(([category, docs]) => {
  //     const state =
  //       category === "travel" ? uploadStateTravel : uploadStateTanker;

  //     if (!state) return;

  //     Object.keys(docs).forEach((docType) => {
  //       const key = docs[docType].key;
  //       const selectedKey = `selected${
  //         key.charAt(0).toUpperCase() + key.slice(1)
  //       }Files`;

  //       const selectedFiles = state[selectedKey] || [];
  //       selectedFiles.forEach((fileDetail) => {
  //         if (fileDetail.file) {
  //           formData.append(docType, fileDetail.file);
  //         }
  //       });
  //     });
  //   });

  //   return formData;
  // };

  const getSelectedProducts = () => {
    return Object.keys(selectedFuels).filter((fuel) => selectedFuels[fuel]);
  };
  // API calls
  const updateVoyageFields = async () => {
    const authKey = localStorage.getItem("authKey");

    const selectedProducts = Object.keys(selectedFuels).filter(
      (fuel) => selectedFuels[fuel]
    );

    const updateData = {
      voyage_number: voyageData.voyage_number,
      date: voyageData.date,
      trip_number: voyageData.trip_number,
      // products: Object.entries(selectedFuels)
      //   .filter(([_, isChecked]) => isChecked)
      //   .map(([fuel]) => fuel),
      // product: JSON.stringify(getSelectedProducts()),
      // product: Object.keys(selectedFuels).filter((fuel) => selectedFuels[fuel])
    };

    const formData = new FormData();
    selectedProducts.forEach((product) => {
      formData.append("product", product); // ✅ multiple keys
    });

    formData.append("voyage_number", voyageData.voyage_number);
    formData.append("date", voyageData.date);
    formData.append("trip_number", voyageData.trip_number);

    const prepareProductsTankData = () => {
      const result = {};
      (addedTags || []).forEach((tag) => {
        const product = tag.product || tag.Product || "ATF";
        const tagNo = tag.tagNo || tag.tag_no || tag.id;
        const tank_value = tag?.category;
        const tag_no_value = tag?.tag_no;

        if (!result[product]) result[product] = [];
        result[product].push({ tank_name: tank_value, tag_no: tag_no_value });
      });
      return result;
    };

    const prepareMsProductTanks = () => {
      const result = {};
      (addedTagsMS || []).forEach((tag) => {
        const product = "MS";
        const tagNo = tag.tagNo || tag.tag_no || tag.id;
        const tank_value = tag?.category;
        const tag_no_value = tag?.tag_no;

        if (!result[product]) result[product] = [];
        result[product].push({ tank_name: tank_value, tag_no: tag_no_value });
      });
      return result;
    };

    const prepareHsdProductTanks = () => {
      const result = {};
      (addedTagsHSD || []).forEach((tag) => {
        const product = "HSD";
        const tagNo = tag.tagNo || tag.tag_no || tag.id;
        const tank_value = tag?.category;
        const tag_no_value = tag?.tag_no;

        if (!result[product]) result[product] = [];
        result[product].push({ tank_name: tank_value, tag_no: tag_no_value });
      });
      return result;
    };

    // ✅ Now this works!
    const productsTankData = {
      ...prepareProductsTankData(),
      ...prepareMsProductTanks(),
      ...prepareHsdProductTanks(),
    };
    formData.append("product_tanks", JSON.stringify(productsTankData));

    const response = await axios.put(
      `${API_URL}/api/atf/voyage/${ID}`,
      formData,
      {
        headers: {
          Authorization: authKey,
          // "Content-Type": "application/json",
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  };

  const uploadVoyageDocuments = async () => {
    const authKey = localStorage.getItem("authKey");
    const formData = prepareDocumentFormData();

    const response = await axios.post(
      `${API_URL}/api/atf/voyage/upload_documents`,
      formData,
      {
        headers: {
          Authorization: authKey,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  };

  // const uploadVoyageDocuments = async () => {
  //   const authKey = localStorage.getItem("authKey");

  //   let formData;

  //   try {
  //     formData = prepareDocumentFormData();
  //   } catch (error) {
  //     console.error("Error preparing FormData:", error.message);
  //     showSnackbar(error.message || "Error preparing files.", "error");
  //     return; // ✅ STOP: Do not call API
  //   }

  //   if (!formData) {
  //     console.warn("No FormData generated. Skipping upload.");
  //     return; // ✅ STOP: Do not call API
  //   }

  //   try {
  //     const response = await axios.post(
  //       `${API_URL}/api/atf/voyage/upload_documents`,
  //       formData,
  //       {
  //         headers: {
  //           Authorization: authKey,
  //           "Content-Type": "multipart/form-data",
  //         },
  //       }
  //     );

  //     return response;
  //   } catch (err) {
  //     console.error("Error uploading documents:", err);
  //     showSnackbar(
  //       err?.response?.data?.message || "Upload failed. Please try again.",
  //       "error"
  //     );
  //     throw err; // rethrow for caller if needed
  //   }
  // };

  // Form validation
  const validateForm = () => {
    const validationErrors = {};

    if (!voyageData.voyage_number?.trim()) {
      validationErrors.voyage_number = "Voyage Number is required.";
    }

    if (!voyageData.date) {
      validationErrors.date = "Date is required.";
    }

    if (!voyageData.trip_number?.trim()) {
      validationErrors.trip_number = "Trip Number is required.";
    }

    const hasSelectedFuel = Object.values(selectedFuels).some(Boolean);
    if (!hasSelectedFuel) {
      validationErrors.product = "At least one product must be selected.";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const MAX_FILE_SIZE_MB = 10;

  const getOversizedFiles = () => {
    const oversized = [];

    // ATF
    for (const docType of Object.keys(documentTypes)) {
      const selectedFiles = getSelectedFiles(docType);
      for (const fileDetail of selectedFiles) {
        const file = fileDetail.file;
        if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          oversized.push(file.name);
        }
      }
    }

    // MS and HSD
    for (const [product, docs] of Object.entries(documentUploadMap)) {
      const state = product === "ms" ? uploadStateMS : uploadStateHSD;

      for (const docType of Object.keys(docs)) {
        const key = docs[docType].key;
        const selectedKey = `selected${
          key.charAt(0).toUpperCase() + key.slice(1)
        }Files`;
        const selectedFiles = state[selectedKey] || [];

        for (const fileDetail of selectedFiles) {
          const file = fileDetail.file;
          if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            oversized.push(file.name);
          }
        }
      }
    }

    // Travel & Tanker
    for (const [category, docs] of Object.entries(documentUploadMapCommon)) {
      const state =
        category === "travel"
          ? uploadStateTravel
          : category === "tanker"
          ? uploadStateTanker
          : null;

      if (!state) continue;

      for (const docType of Object.keys(docs)) {
        const key = docs[docType].key;
        const selectedKey = `selected${
          key.charAt(0).toUpperCase() + key.slice(1)
        }Files`;
        const selectedFiles = state[selectedKey] || [];

        for (const fileDetail of selectedFiles) {
          const file = fileDetail.file;
          if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            oversized.push(file.name);
          }
        }
      }
    }

    return oversized;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      showSnackbar("Please fix the validation errors.", "error");
      return;
    }

    // 🚫 Check for oversized files
    const oversizedFiles = getOversizedFiles();
    if (oversizedFiles.length > 0) {
      const message = `The following files exceed 10 MB and cannot be uploaded:\n- ${oversizedFiles.join(
        "\n- "
      )}`;
      showSnackbar(message, "error");
      return;
    }

    try {
      setIsLoading(true);

      // Always update voyage fields first
      console.log("Updating voyage fields...");
      await updateVoyageFields();
      showSnackbar("Voyage data updated successfully!", "success");

      // Upload documents if any are selected
      // if (hasSelectedDocuments()) {
      console.log("Uploading documents...");
      setIsDocumentLoading(true);

      try {
        await uploadVoyageDocuments();
        showSnackbar(
          "Voyage data and documents updated successfully!",
          "success"
        );
      } catch (docError) {
        console.error("Document upload failed:", docError);
        showSnackbar(
          "Voyage data updated, but document upload failed. Please try uploading documents again.",
          "warning"
        );
      } finally {
        setIsDocumentLoading(false);
      }
      // }

      // Navigate back after successful operations
      setTimeout(() => {
        navigate("/voyages");
      }, 2000);
    } catch (error) {
      console.error("Error updating voyage:", error);
      const errorMessage =
        error.response?.data?.message ||
        "An error occurred while updating voyage data";
      showSnackbar(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // const fetchOptions = async () => {
  //   try {
  //     const authKey = localStorage.getItem("authKey");
  //     const response = await axios.get(`${API_URL}/api/atf/tanks`, {
  //       headers: {
  //         Authorization: authKey,
  //       },
  //     });

  //     const optionsData = response?.data.map((item) => ({
  //       value: item.id,
  //       label: `${item.Category} - ${item.tag_no}`,
  //     }));

  //     console.log("response.dataaaa", response?.data);
  //     console.log("optionsData", optionsData);
  //     setTagOptions(optionsData);
  //     setTagOptionsMS(optionsData);
  //     setTagOptionsHSD(optionsData);

  //     // Handle the case where no options are returned
  //     if (optionsData.length === 0) {
  //       console.warn("No options available from API");
  //     }

  //     // ✅ Return immediately for use in fetchVoyageData
  //     return optionsData;
  //   } catch (error) {
  //     console.error("Error fetching options:", error);
  //   }
  // };

  // const fetchVoyageData = async () => {
  //   try {
  //     const authKey = localStorage.getItem("authKey");
  //     const response = await axios.get(`${API_URL}/api/atf/voyage/${ID}`, {
  //       headers: { Authorization: authKey },
  //     });

  //     const data = response.data;
  //     console.log("Fetched voyage data:", data);

  //     const rawDate = data.date;
  //     const formattedDate = new Date(rawDate).toISOString().split("T")[0];
  //     setVoyageData({
  //       voyage_number: data.voyage_number,
  //       date: formattedDate,
  //       trip_number: data.trip_number,
  //     });

  //     if (Array.isArray(data.products)) {
  //       setSelectedFuels({
  //         ATF: data.products.includes("ATF"),
  //         MS: data.products.includes("MS"),
  //         HSD: data.products.includes("HSD"),
  //       });
  //     } else {
  //       setSelectedFuels({ ATF: false, MS: false, HSD: false });
  //     }

  //     const attachments = data.attachments || [];
  //     const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  //     //-----------------------
  //     // ✅ ATF tanks & tags
  //     //-----------------------
  //     const preselectedAtfTags = [];
  //     if (data?.tanks?.ATF) {
  //       data.tanks.ATF.forEach((tank) => {
  //         const tankNo = tank.tag_no;
  //         const hasFile = attachments.some(
  //           (f) => f.doc_type === `atf_bft_report_${tankNo}`
  //         );
  //         preselectedAtfTags.push({
  //           tagNo: `${tank.tank_name} - ${tankNo}`,
  //           id: tankNo,
  //           tag_no: tankNo,
  //           category: tank.tank_name,
  //           hasFile,
  //         });
  //       });
  //     }
  //     setAddedTags(preselectedAtfTags);
  //     setTagOptions((prev) =>
  //       prev.filter(
  //         (opt) => !preselectedAtfTags.find((tag) => tag.tagNo === opt.label)
  //       )
  //     );

  //     //-----------------------
  //     // ✅ MS tanks & tags
  //     //-----------------------
  //     const preselectedMsTags = [];
  //     if (data?.tanks?.MS) {
  //       data.tanks.MS.forEach((tank) => {
  //         const tankNo = tank.tag_no;
  //         const hasFile = attachments.some(
  //           (f) => f.doc_type === `ms_bft_report_${tankNo}`
  //         );
  //         preselectedMsTags.push({
  //           tagNo: `${tank.tank_name} - ${tankNo}`,
  //           id: tankNo,
  //           tag_no: tankNo,
  //           category: tank.tank_name,
  //           hasFile,
  //         });
  //       });
  //     }
  //     setAddedTagsMS(preselectedMsTags);
  //     setTagOptionsMS((prev) =>
  //       prev.filter(
  //         (opt) => !preselectedMsTags.find((tag) => tag.tagNo === opt.label)
  //       )
  //     );

  //     //-----------------------
  //     // ✅ HSD tanks & tags
  //     //-----------------------
  //     const preselectedHsdTags = [];
  //     if (data?.tanks?.HSD) {
  //       data.tanks.HSD.forEach((tank) => {
  //         const tankNo = tank.tag_no;
  //         const hasFile = attachments.some(
  //           (f) => f.doc_type === `hsd_bft_report_${tankNo}`
  //         );
  //         preselectedHsdTags.push({
  //           tagNo: `${tank.tank_name} - ${tankNo}`,
  //           id: tankNo,
  //           tag_no: tankNo,
  //           category: tank.tank_name,
  //           hasFile,
  //         });
  //       });
  //     }
  //     setAddedTagsHSD(preselectedHsdTags);
  //     setTagOptionsHSD((prev) =>
  //       prev.filter(
  //         (opt) => !preselectedHsdTags.find((tag) => tag.tagNo === opt.label)
  //       )
  //     );

  //     //-----------------------
  //     // ✅ ATF uploaded static & dynamic
  //     //-----------------------
  //     const newDocumentStates = {};
  //     Object.keys(documentTypes).forEach((docType) => {
  //       const key = documentTypes[docType].key;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       newDocumentStates[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     preselectedAtfTags.forEach((tag) => {
  //       const tankNo = tag.tag_no;
  //       const docType = `atf_bft_report_${tankNo}`;
  //       const key = `bftReport_${tankNo}`;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       newDocumentStates[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     setDocumentStates(newDocumentStates);

  //     //-----------------------
  //     // ✅ MS uploaded static & dynamic
  //     //-----------------------
  //     const newUploadStateMS = {};
  //     Object.keys(documentUploadMap.ms).forEach((docType) => {
  //       if (docType.includes("bft_report")) return; // skip dynamic
  //       const key = documentUploadMap.ms[docType].key;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       newUploadStateMS[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     preselectedMsTags.forEach((tag) => {
  //       const tankNo = tag.tag_no;
  //       const docType = `ms_bft_report_${tankNo}`;
  //       const key = `msBftReport_${tankNo}`;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       newUploadStateMS[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     setUploadStateMS(newUploadStateMS);

  //     //-----------------------
  //     // ✅ HSD uploaded static & dynamic
  //     //-----------------------
  //     const newUploadStateHSD = {};
  //     Object.keys(documentUploadMap.hsd).forEach((docType) => {
  //       if (docType.includes("bft_report")) return; // skip dynamic
  //       const key = documentUploadMap.hsd[docType].key;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       newUploadStateHSD[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     preselectedHsdTags.forEach((tag) => {
  //       const tankNo = tag.tag_no;
  //       const docType = `hsd_bft_report_${tankNo}`;
  //       const key = `hsdBftReport_${tankNo}`;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       newUploadStateHSD[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     setUploadStateHSD(newUploadStateHSD);

  //     //-----------------------
  //     // ✅ Travel & Tanker uploaded
  //     //-----------------------
  //     const travelFiles = {};
  //     Object.keys(documentUploadMapCommon.travel).forEach((docType) => {
  //       const key = documentUploadMapCommon.travel[docType].key;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       travelFiles[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     setUploadStateTravel(travelFiles);

  //     const tankerFiles = {};
  //     Object.keys(documentUploadMapCommon.tanker).forEach((docType) => {
  //       const key = documentUploadMapCommon.tanker[docType].key;
  //       const uploadedKey = `uploaded${capitalize(key)}Files`;
  //       tankerFiles[uploadedKey] = attachments.filter(
  //         (a) => a.doc_type === docType
  //       );
  //     });
  //     setUploadStateTanker(tankerFiles);
  //   } catch (err) {
  //     console.error("Error fetching voyage data", err);
  //     showSnackbar("Failed to load voyage data", "error");
  //   }
  // };

  // useEffect(() => {
  //   const init = async () => {
  //     const options = await fetchOptions(); // ✅ gets your tank options
  //     await fetchVoyageData(options); // ✅ pass them directly so .find() works
  //   };

  //   if (ID) {
  //     init();
  //   }
  // }, [ID]);

  useEffect(() => {
    if (ID) {
      fetchVoyageData();
    }
  }, [ID]);

  const authKey = localStorage.getItem("authKey");

  // ✅ Fetch tanks for one category
  const fetchOptions = async (category) => {
    const response = await axios.get(`${API_URL}/api/atf/tanks`, {
      headers: { Authorization: authKey },
      params: { category },
    });

    return response?.data.map((item) => ({
      value: item.id,
      label: `${item.Category} - ${item.tag_no}`,
    }));
  };

  // ✅ Main voyage fetch
  const fetchVoyageData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/atf/voyage/${ID}`, {
        headers: { Authorization: authKey },
      });

      const data = response.data;
      console.log("Fetched voyage data:", data);

      const formattedDate = new Date(data.date).toISOString().split("T")[0];
      setVoyageData({
        voyage_number: data.voyage_number,
        date: formattedDate,
        trip_number: data.trip_number,
      });

      const selectedProducts = Array.isArray(data.products)
        ? data.products
        : [];
      setSelectedFuels({
        ATF: selectedProducts.includes("ATF"),
        MS: selectedProducts.includes("MS"),
        HSD: selectedProducts.includes("HSD"),
      });

      // ✅ Fetch tanks per product type
      if (selectedProducts.includes("ATF")) {
        const atfOptions = await fetchOptions("ATF Tank");
        setTagOptions(atfOptions);
      }
      if (selectedProducts.includes("MS")) {
        const msOptions = await fetchOptions("MS Tank");
        setTagOptionsMS(msOptions);
      }
      if (selectedProducts.includes("HSD")) {
        const hsdOptions = await fetchOptions("HSD Tank");
        setTagOptionsHSD(hsdOptions);
      }

      // ✅ Preselect tanks
      const attachments = data.attachments || [];
      const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

      //-----------------------
      // ATF tanks & options
      //-----------------------
      const preselectedAtfTags = [];
      if (data?.tanks?.ATF) {
        data.tanks.ATF.forEach((tank) => {
          const tankNo = tank.tag_no;
          const hasFile = attachments.some(
            (f) => f.doc_type === `atf_bft_report_${tankNo}`
          );
          preselectedAtfTags.push({
            tagNo: `${tank.tank_name} - ${tankNo}`,
            id: tankNo,
            tag_no: tankNo,
            category: tank.tank_name,
            hasFile,
          });
        });
        setAddedTags(preselectedAtfTags);
        setTagOptions((prev) =>
          prev.filter(
            (opt) => !preselectedAtfTags.find((tag) => tag.tagNo === opt.label)
          )
        );
      }

      //-----------------------
      // MS tanks & options
      //-----------------------
      const preselectedMsTags = [];
      if (data?.tanks?.MS) {
        data.tanks.MS.forEach((tank) => {
          const tankNo = tank.tag_no;
          const hasFile = attachments.some(
            (f) => f.doc_type === `ms_bft_report_${tankNo}`
          );
          preselectedMsTags.push({
            tagNo: `${tank.tank_name} - ${tankNo}`,
            id: tankNo,
            tag_no: tankNo,
            category: tank.tank_name,
            hasFile,
          });
        });
        setAddedTagsMS(preselectedMsTags);
        setTagOptionsMS((prev) =>
          prev.filter(
            (opt) => !preselectedMsTags.find((tag) => tag.tagNo === opt.label)
          )
        );
      }

      //-----------------------
      // HSD tanks & options
      //-----------------------
      const preselectedHsdTags = [];
      if (data?.tanks?.HSD) {
        data.tanks.HSD.forEach((tank) => {
          const tankNo = tank.tag_no;
          const hasFile = attachments.some(
            (f) => f.doc_type === `hsd_bft_report_${tankNo}`
          );
          preselectedHsdTags.push({
            tagNo: `${tank.tank_name} - ${tankNo}`,
            id: tankNo,
            tag_no: tankNo,
            category: tank.tank_name,
            hasFile,
          });
        });
        setAddedTagsHSD(preselectedHsdTags);
        setTagOptionsHSD((prev) =>
          prev.filter(
            (opt) => !preselectedHsdTags.find((tag) => tag.tagNo === opt.label)
          )
        );
      }

      //-----------------------
      // Documents ATF static + dynamic
      //-----------------------
      const newDocumentStates = {};
      Object.keys(documentTypes).forEach((docType) => {
        const key = documentTypes[docType].key;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        newDocumentStates[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      preselectedAtfTags.forEach((tag) => {
        const tankNo = tag.tag_no;
        const docType = `atf_bft_report_${tankNo}`;
        const key = `bftReport_${tankNo}`;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        newDocumentStates[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      setDocumentStates(newDocumentStates);

      //-----------------------
      // MS static + dynamic
      //-----------------------
      const newUploadStateMS = {};
      Object.keys(documentUploadMap.ms).forEach((docType) => {
        if (docType.includes("bft_report")) return; // skip dynamic
        const key = documentUploadMap.ms[docType].key;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        newUploadStateMS[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      preselectedMsTags.forEach((tag) => {
        const tankNo = tag.tag_no;
        const docType = `ms_bft_report_${tankNo}`;
        const key = `msBftReport_${tankNo}`;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        newUploadStateMS[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      setUploadStateMS(newUploadStateMS);

      //-----------------------
      // HSD static + dynamic
      //-----------------------
      const newUploadStateHSD = {};
      Object.keys(documentUploadMap.hsd).forEach((docType) => {
        if (docType.includes("bft_report")) return; // skip dynamic
        const key = documentUploadMap.hsd[docType].key;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        newUploadStateHSD[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      preselectedHsdTags.forEach((tag) => {
        const tankNo = tag.tag_no;
        const docType = `hsd_bft_report_${tankNo}`;
        const key = `hsdBftReport_${tankNo}`;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        newUploadStateHSD[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      setUploadStateHSD(newUploadStateHSD);

      //-----------------------
      // Travel + Tanker static
      //-----------------------
      const travelFiles = {};
      Object.keys(documentUploadMapCommon.travel).forEach((docType) => {
        const key = documentUploadMapCommon.travel[docType].key;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        travelFiles[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      setUploadStateTravel(travelFiles);

      const tankerFiles = {};
      Object.keys(documentUploadMapCommon.tanker).forEach((docType) => {
        const key = documentUploadMapCommon.tanker[docType].key;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        tankerFiles[uploadedKey] = attachments.filter(
          (a) => a.doc_type === docType
        );
      });
      setUploadStateTanker(tankerFiles);
    } catch (err) {
      console.error("Error fetching voyage data", err);
    }
  };

  // Event handlers
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setVoyageData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleDateChange = (date) => {
    const formattedDate = date ? date.toISOString().split("T")[0] : null;

    setVoyageData((prevData) => ({
      ...prevData,
      date: formattedDate,
    }));

    setErrors((prevState) => ({
      ...prevState,
      date: "",
    }));
  };

  const handleCancelClick = () => {
    navigate("/voyages");
  };

  // File handling functions
  // const handleFileChange = (docType, event) => {
  //   const file = event.target.files[0];
  //   if (!file) return;

  //   const key = documentTypes[docType].key;
  //   const selectedKey = `selected${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;

  //   const newFileDetail = {
  //     name: file.name,
  //     size: file.size,
  //     file: file,
  //   };

  //   setDocumentStates((prev) => ({
  //     ...prev,
  //     [selectedKey]: [newFileDetail],
  //     [`uploaded${key.charAt(0).toUpperCase() + key.slice(1)}Files`]: [],
  //   }));
  // };

  const handleFileChange = (docType, event, overrideConfig = null) => {
    const file = event.target.files[0];
    if (!file) return;

    const key = overrideConfig?.key || documentTypes[docType]?.key;

    if (!key) {
      console.warn(`handleFileChange: Unknown docType: ${docType}`);
      return;
    }

    const selectedKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;

    const newFileDetail = {
      name: file.name,
      size: file.size,
      file: file,
    };

    setDocumentStates((prev) => ({
      ...prev,
      [selectedKey]: [newFileDetail],
      [`uploaded${key.charAt(0).toUpperCase() + key.slice(1)}Files`]: [],
    }));
  };

  // const handleUploadClick = (docType) => {
  //   const key = documentTypes[docType].key;
  //   const fileRef = fileInputRefs[key];
  //   if (fileRef?.current) {
  //     fileRef.current.click();
  //   }
  // };

  // ------------
  // const handleUploadClick = (docType, overrideConfig = null) => {
  //   const key = overrideConfig?.key || documentTypes[docType]?.key;

  //   if (!key) {
  //     console.warn(`handleUploadClick: Unknown docType: ${docType}`);
  //     return;
  //   }

  //   const fileRef = fileInputRefs[key];
  //   if (fileRef?.current) {
  //     fileRef.current.click();
  //   } else {
  //     console.warn(`handleUploadClick: No file ref found for key: ${key}`);
  //   }
  // };

  const handleUploadClick = (docType, overrideConfig = {}) => {
    const staticConfig = documentTypes[docType];
    const key = staticConfig?.key || overrideConfig.key;

    const inputRef = staticConfig
      ? fileInputRefs[key]
      : fileInputRefsDynamic.current[key] || createDynamicRef(key);

    inputRef.current?.click();
  };

  const handleSingleUploadInput = (product, docType, event, setState) => {
    const file = event.target.files[0];
    if (!file) return;

    const key = documentUploadMapCommon[product][docType].key;
    const selectedKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;

    const fileDetail = {
      name: file.name,
      size: file.size,
      file,
    };

    setState((prev) => ({
      ...prev,
      [selectedKey]: [fileDetail],
    }));
  };

  // Helper functions for file management
  // const getSelectedFiles = (docType) => {
  //   const key = documentTypes[docType].key;
  //   const selectedKey = `selected${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   return documentStates[selectedKey] || [];
  // };

  const getSelectedFiles = (docType, overrideConfig = null) => {
    const key = overrideConfig?.key || documentTypes[docType]?.key;

    if (!key) {
      console.error(`getSelectedFiles: Missing key for docType=${docType}`);
      return [];
    }

    const selectedKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    return documentStates[selectedKey] || [];
  };

  // const getUploadedFiles = (docType) => {
  //   const key = documentTypes[docType].key;
  //   const uploadedKey = `uploaded${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   return documentStates[uploadedKey] || [];
  // };
  const getUploadedFiles = (docType, overrideConfig = null) => {
    console.log("overrideConfig", overrideConfig);
    const key = overrideConfig?.key || documentTypes[docType]?.key;

    if (!key) {
      console.warn(`getUploadedFiles: Unknown docType: ${docType}`);
      return [];
    }

    const uploadedKey = `uploaded${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    return documentStates[uploadedKey] || [];
  };
  // const getTotalFileCount = (docType) => {
  //   return getSelectedFiles(docType).length + getUploadedFiles(docType).length;
  // };

  const getTotalFileCount = (docType, overrideConfig = null) => {
    return (
      getSelectedFiles(docType, overrideConfig).length +
      getUploadedFiles(docType, overrideConfig).length
    );
  };

  // Popup management
  // const togglePopupAtf = (docType) => {
  //   const key = documentTypes[docType].key;
  //   const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

  //   setDocumentStates((prev) => ({
  //     ...prev,
  //     [popupKey]: !prev[popupKey],
  //   }));
  // };

  const togglePopupAtf = (docType, overrideConfig = null) => {
    const key = overrideConfig?.key || documentTypes[docType]?.key;
    if (!key) return;

    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
    setDocumentStates((prev) => ({
      ...prev,
      [popupKey]: !prev[popupKey],
    }));
  };

  // const closePopup = (docType) => {
  //   const key = documentTypes[docType].key;
  //   const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

  //   setDocumentStates((prev) => ({
  //     ...prev,
  //     [popupKey]: false,
  //   }));
  // };
  const closePopup = (docType, overrideConfig = null) => {
    const key = overrideConfig?.key || documentTypes[docType]?.key;
    if (!key) return;

    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
    setDocumentStates((prev) => ({
      ...prev,
      [popupKey]: false,
    }));
  };

  // const isPopupOpen = (docType) => {
  //   const key = documentTypes[docType].key;
  //   const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
  //   return documentStates[popupKey];
  // };

  const isPopupOpen = (docType, overrideConfig = null) => {
    const key = overrideConfig?.key || documentTypes[docType]?.key;
    if (!key) return false;

    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
    return documentStates[popupKey];
  };

  // File deletion handlers
  const handleDeleteFile = (docType, index, isUploaded = false) => {
    const key = documentTypes[docType].key;
    const fileKey = isUploaded
      ? `uploaded${key.charAt(0).toUpperCase() + key.slice(1)}Files`
      : `selected${key.charAt(0).toUpperCase() + key.slice(1)}Files`;

    setDocumentStates((prev) => {
      const updatedFiles = prev[fileKey].filter((_, i) => i !== index);
      const newState = {
        ...prev,
        [fileKey]: updatedFiles,
      };

      const selectedKey = `selected${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      const uploadedKey = `uploaded${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      const popupKey = `is${
        key.charAt(0).toUpperCase() + key.slice(1)
      }PopupOpen`;

      if (
        newState[selectedKey].length === 0 &&
        newState[uploadedKey].length === 0
      ) {
        newState[popupKey] = false;
      }

      return newState;
    });
  };

  const toggleSingleUploadPopup = (product, docType, setState, state) => {
    const key = documentUploadMapCommon[product][docType].key;
    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
    setState({ ...state, [popupKey]: !state[popupKey] });
  };

  const removeSingleUploadedFile = (product, docType, index, setState) => {
    const key = documentUploadMapCommon[product][docType].key;
    const fileKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

    setState((prev) => {
      const newFiles = (prev[fileKey] || []).filter((_, i) => i !== index);
      const newState = { ...prev, [fileKey]: newFiles };
      if (newFiles.length === 0) newState[popupKey] = false;
      return newState;
    });
  };

  // Render functions
  // const renderUploadSection = (docType) => {
  //   const config = documentTypes[docType];
  //   const key = config.key;
  //   const totalFiles = getTotalFileCount(docType);
  //   const selectedFiles = getSelectedFiles(docType);
  //   const uploadedFiles = getUploadedFiles(docType);

  //   return (
  //     <div key={docType} className="upload-section form-group">
  //       <label
  //         htmlFor={`${key}-input`}
  //         className="upload-label"
  //         style={{ width: "auto" }}
  //       >
  //         {config.label}:
  //       </label>

  //       <div className="upload-controls">
  //         <button
  //           type="button"
  //           className="challan-btn"
  //           onClick={() => handleUploadClick(docType)}
  //         >
  //           <FiUpload className="upload-icon" /> Upload File
  //         </button>

  //         <input
  //           id={`${key}-input`}
  //           type="file"
  //           ref={fileInputRefs[key]}
  //           className="upload-hidden-input"
  //           onChange={(e) => handleFileChange(docType, e)}
  //         />

  //         {totalFiles > 0 && (
  //           <span
  //             className="inquiry-file-btn"
  //             onClick={() => togglePopupAtf(docType)}
  //           >
  //             <MdAttachFile className="file-icon" />
  //             {totalFiles}
  //           </span>
  //         )}
  //       </div>

  //       {isPopupOpen(docType) && (
  //         <div className="attachment-popup" ref={popupRef}>
  //           <div className="popup-content">
  //             <div className="popup-header">
  //               <h4 className="popup-title">Attachments</h4>
  //               <button
  //                 className="popup-close-btn"
  //                 onClick={() => closePopup(docType)}
  //               >
  //                 <IoCloseOutline className="close-icon" />
  //               </button>
  //             </div>

  //             <div className="popup-body">
  //               {uploadedFiles.map((file, index) => (
  //                 <div className="file-item" key={`uploaded-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.filename || file.name}</p>
  //                     <p className="file-size">
  //                       File Size:{" "}
  //                       {((file.file_size || file.size) / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                     {/* <Tooltip title="Download" arrow>
  //                       <button className="file-action-btn download-btn">
  //                         <FiDownload className="action-icon" />
  //                       </button>
  //                     </Tooltip> */}
  //                     {/* <Tooltip title="Delete" arrow>
  //                       <button
  //                         className="file-action-btn remove-btn"
  //                         onClick={() => handleDeleteFile(docType, index, true)}
  //                       >
  //                         <MdDeleteOutline className="action-icon" />
  //                       </button>
  //                     </Tooltip> */}
  //                   </div>
  //                 </div>
  //               ))}

  //               {selectedFiles.map((file, index) => (
  //                 <div className="file-item" key={`selected-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.name}</p>
  //                     <p className="file-size">
  //                       File Size: {(file.size / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                   </div>
  //                 </div>
  //               ))}
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  // const renderUploadSection = (docType, overrideConfig = {}) => {
  //   // Try to find static config
  //   const staticConfig = documentTypes[docType];

  //   // For dynamic tank BFT reports, staticConfig will be undefined → fallback to overrideConfig
  //   const key = staticConfig?.key || overrideConfig.key;
  //   const label = staticConfig?.label || overrideConfig.label || docType;

  //   if (!key) {
  //     console.error(`Missing key for docType: ${docType}`);
  //     return null;
  //   }

  //   const totalFiles = getTotalFileCount(docType, overrideConfig);
  //   const selectedFiles = getSelectedFiles(docType, overrideConfig);
  //   const uploadedFiles = getUploadedFiles(docType, overrideConfig);

  //   return (
  //     <div key={docType} className="upload-section form-group">
  //       <label
  //         htmlFor={`${key}-input`}
  //         className="upload-label"
  //         style={{ width: "auto" }}
  //       >
  //         {label}:
  //       </label>

  //       <div className="upload-controls">
  //         <button
  //           type="button"
  //           className="challan-btn"
  //           onClick={() => handleUploadClick(docType, overrideConfig)}
  //           // disabled={!overrideConfig.isEditMode}
  //         >
  //           <FiUpload className="upload-icon" /> Upload File
  //         </button>

  //         <input
  //           id={`${key}-input`}
  //           type="file"
  //           ref={fileInputRefs[key]}
  //           className="upload-hidden-input"
  //           onChange={(e) => handleFileChange(docType, e, overrideConfig)}
  //         />

  //         {totalFiles > 0 && (
  //           <span
  //             className="inquiry-file-btn"
  //             onClick={() => togglePopupAtf(docType, overrideConfig)}
  //           >
  //             <MdAttachFile className="file-icon" />
  //             {totalFiles}
  //           </span>
  //         )}
  //       </div>

  //       {isPopupOpen(docType, overrideConfig) && (
  //         <div className="attachment-popup" ref={popupRef}>
  //           <div className="popup-content">
  //             <div className="popup-header">
  //               <h4 className="popup-title">Attachments</h4>
  //               <button
  //                 className="popup-close-btn"
  //                 onClick={() => closePopup(docType, overrideConfig)}
  //               >
  //                 <IoCloseOutline className="close-icon" />
  //               </button>
  //             </div>

  //             <div className="popup-body">
  //               {uploadedFiles.map((file, index) => (
  //                 <div className="file-item" key={`uploaded-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.filename || file.name}</p>
  //                     <p className="file-size">
  //                       File Size:{" "}
  //                       {((file.file_size || file.size) / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                   </div>
  //                 </div>
  //               ))}

  //               {selectedFiles.map((file, index) => (
  //                 <div className="file-item" key={`selected-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.name}</p>
  //                     <p className="file-size">
  //                       File Size: {(file.size / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                   </div>
  //                 </div>
  //               ))}
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const renderUploadSection = (docType, overrideConfig = {}) => {
    const staticConfig = documentTypes[docType];

    const key = staticConfig?.key || overrideConfig.key;
    const label = staticConfig?.label || overrideConfig.label || docType;

    if (!key) {
      console.error(`Missing key for docType: ${docType}`);
      return null;
    }

    const inputRef = staticConfig
      ? fileInputRefs[key]
      : fileInputRefsDynamic.current[key] || createDynamicRef(key);

    const totalFiles = getTotalFileCount(docType, overrideConfig);
    const selectedFiles = getSelectedFiles(docType, overrideConfig);
    const uploadedFiles = getUploadedFiles(docType, overrideConfig);

    return (
      <div key={docType} className="upload-section form-group">
        <label
          htmlFor={`${key}-input`}
          className="upload-label"
          style={{ width: "auto" }}
        >
          {label}:
        </label>

        <div className="upload-controls">
          <button
            type="button"
            className="challan-btn"
            onClick={() => handleUploadClick(docType, overrideConfig)}
          >
            <FiUpload className="upload-icon" /> Upload File
          </button>

          <input
            id={`${key}-input`}
            type="file"
            ref={inputRef}
            className="upload-hidden-input"
            onChange={(e) => handleFileChange(docType, e, overrideConfig)}
          />

          {totalFiles > 0 && (
            <span
              className="inquiry-file-btn"
              onClick={() => togglePopupAtf(docType, overrideConfig)}
            >
              <MdAttachFile className="file-icon" />
              {totalFiles}
            </span>
          )}
        </div>

        {isPopupOpen(docType, overrideConfig) && (
          <div className="attachment-popup" ref={popupRef}>
            <div className="popup-content">
              <div className="popup-header">
                <h4 className="popup-title">Attachments</h4>
                <button
                  className="popup-close-btn"
                  onClick={() => closePopup(docType, overrideConfig)}
                >
                  <IoCloseOutline className="close-icon" />
                </button>
              </div>

              <div className="popup-body">
                {uploadedFiles.map((file, index) => (
                  <div className="file-item" key={`uploaded-${index}`}>
                    <div className="file-info">
                      <p className="file-name">{file.filename || file.name}</p>
                      <p className="file-size">
                        File Size:{" "}
                        {((file.file_size || file.size) / 1024).toFixed(2)} KB
                        {console.log("file1", file)}
                      </p>
                    </div>
                    <div className="file-actions">
                      <Tooltip title="View" arrow>
                        <button
                          className="file-action-btn view-btn"
                          type="button"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}

                {selectedFiles.map((file, index) => (
                  <div className="file-item" key={`selected-${index}`}>
                    <div className="file-info">
                      <p className="file-name">{file.name}</p>
                      <p className="file-size">
                        File Size: {(file.size / 1024).toFixed(2)} KB
                        {console.log("file3", file)}
                      </p>
                    </div>
                    <div className="file-actions">
                      <Tooltip title="View" arrow>
                        <button
                          className="file-action-btn view-btn"
                          type="button"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSingleFileUpload = (product, docType, refs, state, setState) => {
    const config = documentUploadMapCommon[product][docType];
    const key = config.key;
    const selectedKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    const uploadedKey = `uploaded${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
    const files = state[selectedKey] || [];
    const selectedFiles = state[selectedKey] || [];
    const uploadedFiles = state[uploadedKey] || [];
    const totalFiles = selectedFiles.length + uploadedFiles.length;
    return (
      <div className="form-group" key={docType}>
        <label htmlFor={`${key}-input`} className="upload-label">
          {config.label}:
        </label>

        <div className="upload-controls">
          <button
            type="button"
            className="challan-btn"
            onClick={() => refs[key]?.current?.click()}
          >
            <FiUpload className="upload-icon" /> Upload File
          </button>

          <input
            id={`${key}-input`}
            type="file"
            ref={refs[key]}
            className="upload-hidden-input"
            onChange={(e) =>
              handleSingleUploadInput(product, docType, e, setState)
            }
          />

          {totalFiles > 0 && (
            <span
              className="inquiry-file-btn"
              onClick={() =>
                toggleSingleUploadPopup(product, docType, setState, state)
              }
            >
              <MdAttachFile className="file-icon" />
              {totalFiles}
            </span>
          )}

          {/* {files.length > 0 && (
            <span
              className="inquiry-file-btn"
              onClick={() =>
                toggleSingleUploadPopup(product, docType, setState, state)
              }
            >
              <MdAttachFile className="file-icon" /> {files.length}
            </span>
          )} */}
        </div>

        {state[popupKey] && (
          <div className="attachment-popup" ref={popupRef}>
            <div className="popup-content">
              <div className="popup-header">
                <h4 className="popup-title">Attachments</h4>
                <button
                  className="popup-close-btn"
                  onClick={() =>
                    toggleSingleUploadPopup(product, docType, setState, state)
                  }
                >
                  <IoCloseOutline className="close-icon" />
                </button>
              </div>
              <div className="popup-body">
                {uploadedFiles.map((file, index) => (
                  <div className="file-item" key={`uploaded-${index}`}>
                    <div className="file-info">
                      <p className="file-name">{file.filename || file.name}</p>
                      <p className="file-size">
                        File Size:{" "}
                        {((file.file_size || file.size) / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <div className="file-actions">
                      <Tooltip title="View" arrow>
                        <button
                          className="file-action-btn view-btn"
                          type="button"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}

                {/* Show selected files */}
                {selectedFiles.map((file, index) => (
                  <div className="file-item" key={`selected-${index}`}>
                    <div className="file-info">
                      <p className="file-name">{file.name}</p>
                      <p className="file-size">
                        File Size: {(file.size / 1024).toFixed(2)} KB
                        {console.log("file4", file)}
                      </p>
                    </div>
                    <div className="file-actions">
                      <Tooltip title="View" arrow>
                        <button
                          className="file-action-btn view-btn"
                          type="button"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}

                {/* {files.map((file, index) => (
                  <div className="file-item" key={index}>
                    <div className="file-info">
                      <p className="file-name">{file.name}</p>
                      <p className="file-size">
                        File Size: {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                ))} */}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // You'll also need to add the missing renderFileUploadFieldMap function
  // that's referenced in your JSX but not defined in the provided code:

  // const renderFileUploadFieldMap = (
  //   product,
  //   docType,
  //   refs,
  //   state,
  //   setState
  // ) => {
  //   const config = documentUploadMap[product][docType];
  //   const key = config.key;
  //   const selectedKey = `selected${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   const uploadedKey = `uploaded${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

  //   const selectedFiles = state[selectedKey] || [];
  //   const uploadedFiles = state[uploadedKey] || [];
  //   const totalFiles = selectedFiles.length + uploadedFiles.length;
  //   console.log("uploadedFiles", uploadedFiles);
  //   return (
  //     <div className="upload-section form-group" key={docType}>
  //       <label
  //         htmlFor={`${key}-input`}
  //         className="upload-label"
  //         style={{ width: "auto" }}
  //       >
  //         {config.label}:
  //       </label>

  //       <div className="upload-controls">
  //         <button
  //           type="button"
  //           className="challan-btn"
  //           onClick={() => refs[key]?.current?.click()}
  //         >
  //           <FiUpload className="upload-icon" /> Upload File
  //         </button>

  //         <input
  //           id={`${key}-input`}
  //           type="file"
  //           ref={refs[key]}
  //           className="upload-hidden-input"
  //           onChange={(e) =>
  //             handleProductFileChange(product, docType, e, setState)
  //           }
  //         />

  //         {totalFiles > 0 && (
  //           <span
  //             className="inquiry-file-btn"
  //             onClick={() =>
  //               toggleProductPopup(product, docType, setState, state)
  //             }
  //           >
  //             <MdAttachFile className="file-icon" />
  //             {totalFiles}
  //           </span>
  //         )}
  //       </div>

  //       {state[popupKey] && (
  //         <div className="attachment-popup" ref={popupRef}>
  //           <div className="popup-content">
  //             <div className="popup-header">
  //               <h4 className="popup-title">Attachments</h4>
  //               <button
  //                 className="popup-close-btn"
  //                 onClick={() =>
  //                   toggleProductPopup(product, docType, setState, state)
  //                 }
  //               >
  //                 <IoCloseOutline className="close-icon" />
  //               </button>
  //             </div>

  //             <div className="popup-body">
  //               {/* Show uploaded files first */}
  //               {uploadedFiles.map((file, index) => (
  //                 <div className="file-item" key={`uploaded-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.filename || file.name}</p>
  //                     <p className="file-size">
  //                       File Size:{" "}
  //                       {((file.file_size || file.size) / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                     {/* <Tooltip title="Download" arrow>
  //                       <button className="file-action-btn download-btn">
  //                         <FiDownload className="action-icon" />
  //                       </button>
  //                     </Tooltip> */}
  //                     {/* <Tooltip title="Delete" arrow>
  //                       <button
  //                         className="file-action-btn remove-btn"
  //                         onClick={() =>
  //                           handleDeleteProductFile(
  //                             product,
  //                             docType,
  //                             index,
  //                             true,
  //                             setState
  //                           )
  //                         }
  //                       >
  //                         <MdDeleteOutline className="action-icon" />
  //                       </button>
  //                     </Tooltip> */}
  //                   </div>
  //                 </div>
  //               ))}

  //               {/* Show selected files */}
  //               {selectedFiles.map((file, index) => (
  //                 <div className="file-item" key={`selected-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.name}</p>
  //                     <p className="file-size">
  //                       File Size: {(file.size / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                   </div>
  //                 </div>
  //               ))}
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  // const renderFileUploadFieldMap = (
  //   product,
  // docType,
  // staticRefs,
  // dynamicRefs,
  // state,
  // setState,
  // configOverride = null
  // ) => {
  //   console.log("refsrefs", refs);
  //   // 🗂️ If dynamic → configOverride used
  //   const config = configOverride || documentUploadMap[product][docType];

  //   if (!config) {
  //     console.error(`Missing config for docType: ${docType}`);
  //     return null;
  //   }

  //   const key = config.key;
  //   const selectedKey = `selected${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   const uploadedKey = `uploaded${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

  //   const selectedFiles = state[selectedKey] || [];
  //   const uploadedFiles = state[uploadedKey] || [];
  //   const totalFiles = selectedFiles.length + uploadedFiles.length;

  //   return (
  //     <div className="upload-section form-group" key={docType}>
  //       <label
  //         htmlFor={`${key}-input`}
  //         className="upload-label"
  //         style={{ width: "auto" }}
  //       >
  //         {config.label}:
  //       </label>

  //       <div className="upload-controls">
  //         <button
  //           type="button"
  //           className="challan-btn"
  //           onClick={() => refs[key]?.current?.click()}
  //           // onClick={() => refs.current[key]?.click()}
  //         >
  //           <FiUpload className="upload-icon" /> Upload File
  //         </button>

  //         <input
  //           id={`${key}-input`}
  //           type="file"
  //           ref={refs[key]}
  //           className="upload-hidden-input"
  //           onChange={(e) =>
  //             handleProductFileChange(
  //               product,
  //               docType,
  //               e,
  //               setState,
  //               configOverride
  //             )
  //           }
  //         />

  //         {totalFiles > 0 && (
  //           <span
  //             className="inquiry-file-btn"
  //             onClick={() =>
  //               toggleProductPopup(product, docType, setState, state, config)
  //             }
  //           >
  //             <MdAttachFile className="file-icon" />
  //             {totalFiles}
  //           </span>
  //         )}
  //       </div>

  //       {state[popupKey] && (
  //         <div className="attachment-popup" ref={popupRef}>
  //           <div className="popup-content">
  //             <div className="popup-header">
  //               <h4 className="popup-title">Attachments</h4>
  //               <button
  //                 className="popup-close-btn"
  //                 onClick={() =>
  //                   toggleProductPopup(
  //                     product,
  //                     docType,
  //                     setState,
  //                     state,
  //                     config
  //                   )
  //                 }
  //               >
  //                 <IoCloseOutline className="close-icon" />
  //               </button>
  //             </div>

  //             <div className="popup-body">
  //               {uploadedFiles.map((file, index) => (
  //                 <div className="file-item" key={`uploaded-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.filename || file.name}</p>
  //                     <p className="file-size">
  //                       File Size:{" "}
  //                       {((file.file_size || file.size) / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                   </div>
  //                 </div>
  //               ))}

  //               {selectedFiles.map((file, index) => (
  //                 <div className="file-item" key={`selected-${index}`}>
  //                   <div className="file-info">
  //                     <p className="file-name">{file.name}</p>
  //                     <p className="file-size">
  //                       File Size: {(file.size / 1024).toFixed(2)} KB
  //                     </p>
  //                   </div>
  //                   <div className="file-actions">
  //                     <Tooltip title="View" arrow>
  //                       <button
  //                         className="file-action-btn view-btn"
  //                         type="button"
  //                         onClick={() => handleViewAttachment(file.file_id)}
  //                       >
  //                         <FiEye className="action-icon" />
  //                       </button>
  //                     </Tooltip>
  //                   </div>
  //                 </div>
  //               ))}
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const renderFileUploadFieldMap = (
    product,
    docType,
    staticRefs,
    dynamicRefs,
    state,
    setState,
    configOverride = null
  ) => {
    // Pick config
    const config = configOverride || documentUploadMap[product][docType];
    if (!config) {
      console.error(`Missing config for docType: ${docType}`);
      return null;
    }

    const key = config.key;
    const isDynamic = !!configOverride;

    // Pick ref group
    const refObj = isDynamic ? dynamicRefs : staticRefs;

    // Input ref handler
    const inputRef = isDynamic
      ? (el) => {
          if (el) refObj.current[key] = el;
        }
      : refObj[key];

    const handleClick = () =>
      isDynamic ? refObj.current[key]?.click() : refObj[key]?.current?.click();

    const selectedKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    const uploadedKey = `uploaded${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

    const selectedFiles = state[selectedKey] || [];
    const uploadedFiles = state[uploadedKey] || [];
    const totalFiles = selectedFiles.length + uploadedFiles.length;

    return (
      <div className="upload-section form-group" key={docType}>
        <label
          htmlFor={`${key}-input`}
          className="upload-label"
          style={{ width: "auto" }}
        >
          {config.label}:
        </label>

        <div className="upload-controls">
          <button type="button" className="challan-btn" onClick={handleClick}>
            <FiUpload className="upload-icon" /> Upload File
          </button>

          <input
            id={`${key}-input`}
            type="file"
            ref={inputRef}
            className="upload-hidden-input"
            onChange={(e) =>
              handleProductFileChange(
                product,
                docType,
                e,
                setState,
                configOverride
              )
            }
          />

          {totalFiles > 0 && (
            <span
              className="inquiry-file-btn"
              onClick={() =>
                toggleProductPopup(
                  product,
                  docType,
                  setState,
                  state,
                  configOverride || config
                )
              }
            >
              <MdAttachFile className="file-icon" />
              {totalFiles}
            </span>
          )}
        </div>

        {state[popupKey] && (
          <div className="attachment-popup" ref={popupRef}>
            <div className="popup-content">
              <div className="popup-header">
                <h4 className="popup-title">Attachments</h4>
                <button
                  className="popup-close-btn"
                  onClick={() =>
                    toggleProductPopup(
                      product,
                      docType,
                      setState,
                      state,
                      configOverride || config
                    )
                  }
                >
                  <IoCloseOutline className="close-icon" />
                </button>
              </div>

              <div className="popup-body">
                {uploadedFiles.map((file, index) => (
                  <div className="file-item" key={`uploaded-${index}`}>
                    <div className="file-info">
                      <p className="file-name">{file.filename || file.name}</p>
                      <p className="file-size">
                        File Size:{" "}
                        {((file.file_size || file.size) / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <div className="file-actions">
                      <Tooltip title="View" arrow>
                        <button
                          className="file-action-btn view-btn"
                          type="button"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}

                {selectedFiles.map((file, index) => (
                  <div className="file-item" key={`selected-${index}`}>
                    <div className="file-info">
                      <p className="file-name">{file.name}</p>
                      <p className="file-size">
                        File Size: {(file.size / 1024).toFixed(2)} KB
                        {console.log("file7", file)}
                      </p>
                    </div>
                    <div className="file-actions">
                      <Tooltip title="View" arrow>
                        <button
                          className="file-action-btn view-btn"
                          type="button"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Additional helper functions you'll need:

  // const handleProductFileChange = (product, docType, event, setState) => {
  //   const file = event.target.files[0];
  //   if (!file) return;

  //   const key = documentUploadMap[product][docType].key;
  //   const selectedKey = `selected${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;
  //   const uploadedKey = `uploaded${
  //     key.charAt(0).toUpperCase() + key.slice(1)
  //   }Files`;

  //   const newFileDetail = {
  //     name: file.name,
  //     size: file.size,
  //     file: file,
  //   };

  //   setState((prev) => ({
  //     ...prev,
  //     [selectedKey]: [newFileDetail],
  //     [uploadedKey]: [], // Clear uploaded files when new file is selected
  //   }));
  // };

  const handleProductFileChange = (
    product,
    docType,
    event,
    setState,
    configOverride = null
  ) => {
    const file = event.target.files[0];
    if (!file) return;

    // Use override for dynamic BFT
    const config = configOverride || documentUploadMap[product][docType];
    if (!config) {
      console.error(`Missing config for docType: ${docType}`);
      return;
    }

    const key = config.key;
    const selectedKey = `selected${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;
    const uploadedKey = `uploaded${
      key.charAt(0).toUpperCase() + key.slice(1)
    }Files`;

    const newFileDetail = {
      name: file.name,
      size: file.size,
      file: file,
    };

    setState((prev) => ({
      ...prev,
      [selectedKey]: [newFileDetail],
      [uploadedKey]: [], // clear old uploaded when new selected
    }));
  };

  // const toggleProductPopup = (product, docType, setState, state) => {
  //   const key = documentUploadMap[product][docType].key;
  //   const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;
  //   setState({ ...state, [popupKey]: !state[popupKey] });
  // };

  const toggleProductPopup = (
    product,
    docType,
    setState,
    state,
    config = null
  ) => {
    let key;

    if (config) {
      key = config.key; // ✅ Use dynamic key for BFT
    } else {
      key = documentUploadMap[product][docType].key; // ✅ Fallback to static key
    }

    const popupKey = `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`;

    setState({
      ...state,
      [popupKey]: !state[popupKey],
    });
  };

  const handleDeleteProductFile = (
    product,
    docType,
    index,
    isUploaded,
    setState
  ) => {
    const key = documentUploadMap[product][docType].key;
    const fileKey = isUploaded
      ? `uploaded${key.charAt(0).toUpperCase() + key.slice(1)}Files`
      : `selected${key.charAt(0).toUpperCase() + key.slice(1)}Files`;

    setState((prev) => {
      const updatedFiles = prev[fileKey].filter((_, i) => i !== index);
      const newState = {
        ...prev,
        [fileKey]: updatedFiles,
      };

      const selectedKey = `selected${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      const uploadedKey = `uploaded${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Files`;
      const popupKey = `is${
        key.charAt(0).toUpperCase() + key.slice(1)
      }PopupOpen`;

      // Close popup if no files remain
      if (
        newState[selectedKey].length === 0 &&
        newState[uploadedKey].length === 0
      ) {
        newState[popupKey] = false;
      }

      return newState;
    });
  };

  // View attachment function - Fixed version
  const handleViewAttachment = async (fileId) => {
    console.log("fileId", fileId);
    try {
      const authKey = localStorage.getItem("authKey");
      const viewUrl = `${API_URL}/api/voyage/view_voyage_attachment/${fileId}`;

      const response = await axios.get(viewUrl, {
        headers: {
          Authorization: authKey,
        },
        responseType: "blob",
      });

      // Get the content type from response headers
      const contentType =
        response.headers["content-type"] || "application/octet-stream";

      // Create blob with proper content type
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);

      // Open new window and write HTML content for proper viewing
      const newWindow = window.open("", "_blank");

      // Check if it's an image
      if (contentType.startsWith("image/")) {
        newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Image Preview</title>
                <style>
                  body { 
                    margin: 0; 
                    padding: 20px; 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    min-height: 100vh;
                    background-color: #f0f0f0;
                  }
                  img { 
                    max-width: 100%; 
                    max-height: 100vh; 
                    object-fit: contain;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                  }
                </style>
              </head>
              <body>
                <img src="${fileURL}" alt="File Preview" />
              </body>
            </html>
          `);
      }
      // Check if it's a PDF
      else if (contentType === "application/pdf") {
        newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>PDF Preview</title>
                <style>
                  body { margin: 0; padding: 0; }
                  iframe { width: 100%; height: 100vh; border: none; }
                </style>
              </head>
              <body>
                <iframe src="${fileURL}" type="application/pdf"></iframe>
              </body>
            </html>
          `);
      }
      // Check if it's a text file
      else if (contentType.startsWith("text/")) {
        const text = await response.data.text();
        newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Text Preview</title>
                <style>
                  body { 
                    font-family: monospace; 
                    padding: 20px; 
                    background-color: #f8f8f8;
                    line-height: 1.4;
                  }
                  pre { 
                    white-space: pre-wrap; 
                    word-wrap: break-word; 
                  }
                </style>
              </head>
              <body>
                <pre>${text}</pre>
              </body>
            </html>
          `);
      }
      // For other file types, provide download option
      else {
        newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>File Preview</title>
                <style>
                  body { 
                    font-family: Arial, sans-serif; 
                    padding: 20px; 
                    text-align: center;
                    background-color: #f0f0f0;
                  }
                  .container {
                    max-width: 400px;
                    margin: 50px auto;
                    padding: 30px;
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                  }
                  .download-btn {
                    background-color: #007bff;
                    color: white;
                    padding: 10px 20px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    text-decoration: none;
                    display: inline-block;
                    margin-top: 20px;
                  }
                  .download-btn:hover {
                    background-color: #0056b3;
                  }
                </style>
              </head>
              <body>
                <div class="container">
                  <h3>File Preview</h3>
                  <p>This file type cannot be previewed directly.</p>
                  <p>Content Type: ${contentType}</p>
                  <a href="${fileURL}" download class="download-btn">Download File</a>
                </div>
              </body>
            </html>
          `);
      }

      newWindow.document.close();

      // Clean up the object URL after a delay to ensure it's loaded
      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 10000);
    } catch (error) {
      console.error("Error viewing file:", error);
      setSeverity("error");
      showSnackbar("Failed to view file");
    }
  };

  // -------------------

  const handleRemoveTag = (tagId) => {
    // Find the tag being removed
    const removedTag = addedTags.find((tag) => tag.id === tagId);
    if (!removedTag) return;

    // Remove from addedTags
    setAddedTags((prevTags) => prevTags.filter((tag) => tag.id !== tagId));

    // Return tag back to Select dropdown options
    setTagOptions((prevOptions) => [
      ...prevOptions,
      {
        value: removedTag.id,
        label: removedTag.tagNo,
        tag_no: removedTag.tag_no,
        category: removedTag.category,
      },
    ]);

    // Determine dynamic docType key for this tank
    const tankIdentifier = removedTag.tag_no || removedTag.id;
    const tankDocType = `bft_report_${tankIdentifier}`;

    // OPTIONAL: If you store uploaded files for each tank in state,
    // clear the files for this tank so they don’t persist accidentally.
    // setUploadedFiles((prev) => {
    //   const updated = { ...prev };
    //   delete updated[tankDocType];
    //   return updated;
    // });

    // OPTIONAL: If you track popups for each tank, close its popup too.
    // setIsPopupOpen((prev) => ({
    //   ...prev,
    //   [tankDocType]: false,
    // }));
  };

  const handleTagNoChange = (selectedOption) => {
    console.log("selectedOption", selectedOption);
    if (!selectedOption) {
      console.log("No ATF tag selected.");
      return;
    }

    const { value, label } = selectedOption;

    // Parse parts from label: e.g. "ATF Tank - T002"
    const tagNoExtracted = label.split("-").pop().trim(); // "T002"
    const categoryExtracted = label.split("-")[0].trim(); // "ATF Tank"

    setAddedTags((prevTags) => {
      // Prevent duplicates
      const exists = prevTags.some((tag) => tag.id === value);
      if (exists) {
        console.log("Tag already added:", value);
        return prevTags;
      }

      const updatedTags = [
        ...prevTags,
        {
          id: value,
          tagNo: label, // Full label: "ATF Tank - T002"
          tag_no: tagNoExtracted, // Extracted: "T002"
          category: categoryExtracted, // "ATF Tank"
        },
      ];

      console.log("Updated ATF Tags:", updatedTags);
      return updatedTags;
    });

    // Remove selected tag from dropdown options
    setTagOptions((prevOptions) =>
      prevOptions.filter((option) => option.value !== value)
    );

    // Clear dropdown selection
    setSelectedTagNo(null);
  };

  const handleTagNoChangeHSD = (selectedOption) => {
    setSelectedTagNoHSD(selectedOption);
    if (selectedOption) {
      setAddedTagsHSD((prev) => [
        ...prev,
        {
          tagNo: selectedOption.label,
          id: selectedOption.value,
          tag_no: selectedOption.label.split("-").pop().trim(),
          category: selectedOption.label.split("-")[0].trim(),
        },
      ]);
      setTagOptionsHSD((prev) =>
        prev.filter((opt) => opt.value !== selectedOption.value)
      );
      setSelectedTagNoHSD(null);
    }
  };

  const handleRemoveTagHSD = (tagId) => {
    const tagToRemove = addedTagsHSD.find((tag) => tag.id === tagId);
    if (tagToRemove) {
      setTagOptionsHSD((prev) => [
        ...prev,
        { label: tagToRemove.tagNo, value: tagToRemove.id },
      ]);
      setAddedTagsHSD((prev) => prev.filter((tag) => tag.id !== tagId));
    }
  };

  const handleTagNoChangeMS = (selectedOption) => {
    setSelectedTagNoMS(selectedOption);
    if (selectedOption) {
      setAddedTagsMS((prev) => [
        ...prev,
        {
          tagNo: selectedOption.label,
          id: selectedOption.value,
          tag_no: selectedOption.label.split("-").pop().trim(),
          category: selectedOption.label.split("-")[0].trim(),
        },
      ]);
      setTagOptionsMS((prev) =>
        prev.filter((opt) => opt.value !== selectedOption.value)
      );
      setSelectedTagNoMS(null);
    }
  };

  const handleRemoveTagMS = (tagId) => {
    const tagToRemove = addedTagsMS.find((tag) => tag.id === tagId);
    if (tagToRemove) {
      setTagOptionsMS((prev) => [
        ...prev,
        { label: tagToRemove.tagNo, value: tagToRemove.id },
      ]);
      setAddedTagsMS((prev) => prev.filter((tag) => tag.id !== tagId));
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content" ref={containerRef}>
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Edit Voyage</PageBackTitle>
              <div className="data-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form className="form-detail" onKeyDown={handleKeyDown}>
              <div className="new-data-form">
                <div className="">
                  <div className="form-group">
                    <label htmlFor="voyage_number">
                      Voyage Number<span className="text-danger">*</span>
                    </label>
                    <div className="">
                      <input
                        type="text"
                        name="voyage_number"
                        value={voyageData.voyage_number}
                        onChange={handleInputChange}
                        placeholder="Enter Voyage Number"
                        className="half-width-input"
                      />
                    </div>
                    {errors.voyage_number && (
                      <div className="error-message">
                        {errors.voyage_number}
                      </div>
                    )}

                    <div className="voyageform-date">
                      {/* <label>
                      Date:<span className="text-danger">*</span>
                    </label> */}
                      {/* <div className="input-group"> */}
                      <DatePicker
                        // selected={voyageData.date}
                        selected={
                          voyageData.date ? new Date(voyageData.date) : null
                        }
                        onChange={handleDateChange}
                        placeholderText="Select date"
                        dateFormat="dd/MM/yyyy"
                        isClearable
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        //   min={today}
                        className={`${
                          theme === "dark" ? "dark-theme-datepicker" : ""
                        } half-width-input`}
                        style={{ marginLeft: "4rem" }}
                      />
                      {/* </div> */}
                      {errors.date && (
                        <div className="error-message">{errors.date}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="trip_number">
                    Trip Number<span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="trip_number"
                      value={voyageData.trip_number}
                      onChange={handleInputChange}
                      placeholder="Enter Trip Number"
                    />
                  </div>
                  {errors.trip_number && (
                    <div className="error-message">{errors.trip_number}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="doc_name"></label>
                  <div className="input-group">
                    <div className="error-messages125">
                      File size must be 10 MB or less.
                    </div>
                  </div>
                </div>

                {/* Travel Load Port Doc */}
                {renderSingleFileUpload(
                  "travel",
                  "tanker_load_port_doc",
                  travelRefs,
                  uploadStateTravel,
                  // setUploadTravelLordPortCommon
                  setUploadStateTravel
                )}

                {/* Tanker Documentation */}
                {renderSingleFileUpload(
                  "tanker",
                  "tanker_docs",
                  tankerRefs,
                  uploadStateTanker,
                  // setUploadTankerDocumentationCommon
                  setUploadStateTanker
                )}

                <div className="checkbox-form-group ">
                  <label htmlFor="" className="checkbox-form-group-label">
                    Products in Voyage<span className="text-danger">*</span>
                  </label>

                  <div className="fuel-checkbox-group product_dflex">
                    {["ATF", "MS", "HSD"].map((fuel) => (
                      <label key={fuel}>
                        {/* <input
                          className="voyage-checkbox"
                          type="checkbox"
                          name={fuel}
                          checked={selectedFuels[fuel]}
                          onChange={(e) => {
                            const { name, checked } = e.target;
                            setSelectedFuels((prev) => ({
                              ...prev,
                              [name]: checked,
                            }));
                          }}
                        /> */}
                        <input
                          className="voyage-checkbox"
                          type="checkbox"
                          name={fuel}
                          checked={selectedFuels[fuel]}
                          onChange={async (e) => {
                            const { name, checked } = e.target;

                            setSelectedFuels((prev) => ({
                              ...prev,
                              [name]: checked,
                            }));

                            if (checked) {
                              // ✔️ Fetch tanks for this product type
                              let options = [];
                              if (name === "ATF") {
                                options = await fetchOptions("ATF Tank");
                                setTagOptions(options);
                              } else if (name === "MS") {
                                options = await fetchOptions("MS Tank");
                                setTagOptionsMS(options);
                              } else if (name === "HSD") {
                                options = await fetchOptions("HSD Tank");
                                setTagOptionsHSD(options);
                              }
                            } else {
                              // ❌ Clear tanks and selected tags for this fuel
                              if (name === "ATF") {
                                setTagOptions([]);
                                setAddedTags([]);
                              } else if (name === "MS") {
                                setTagOptionsMS([]);
                                setAddedTagsMS([]);
                              } else if (name === "HSD") {
                                setTagOptionsHSD([]);
                                setAddedTagsHSD([]);
                              }
                            }
                          }}
                        />
                        {fuel}
                      </label>
                    ))}
                  </div>
                </div>

                {selectedFuels.ATF && (
                  <div className="product_dflex">
                    <div className="product_label">
                      <label>ATF Documents:</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      {Object.keys(documentTypes).map((docType) => {
                        if (docType === "bft_report") {
                          return (
                            <React.Fragment key={docType}>
                              <div className="input-group-dropdown">
                                <Select
                                  id="TagNo"
                                  options={tagOptions}
                                  value={selectedTagNo}
                                  onChange={handleTagNoChange}
                                  placeholder="Select Tank"
                                  isSearchable
                                  className="tank_dropdown"
                                  isDisabled={!isEditMode}
                                />
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "10px",
                                  marginBottom: "15px",
                                }}
                              >
                                {addedTags.map((tag) => (
                                  <Chip
                                    key={tag.id}
                                    label={tag.tagNo}
                                    onDelete={
                                      isEditMode
                                        ? () => handleRemoveTag(tag.id)
                                        : undefined
                                    }
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </div>

                              {addedTags.map((tag) => {
                                const tankIdentifier = tag.tag_no || tag.id;
                                const tankDocType = `bft_report_${tankIdentifier}`;

                                const dynamicConfig = {
                                  key: `bftReport_${tankIdentifier}`,
                                  label: `BFT Report - ${tag.tagNo}`,
                                  isEditMode: isEditMode, // Pass edit mode if needed
                                };

                                return (
                                  <div
                                    key={tankDocType}
                                    className="tank-specific-upload"
                                  >
                                    {renderUploadSection(
                                      tankDocType,
                                      dynamicConfig
                                    )}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        }

                        return (
                          <React.Fragment key={docType}>
                            {renderUploadSection(docType)}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* {selectedFuels.ATF && (
                  <div className="product_dflex">
                    <div className="product_label">
                      <label>ATF Documents:</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      {Object.keys(documentTypes).map((docType) =>
                        renderUploadSection(docType)
                      )}
                    </div>
                  </div>
                )} */}

                {selectedFuels.MS && (
                  <div className="product_dflex">
                    <div className="product_label">
                      <label htmlFor="">MS Documents :</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      {Object.keys(documentUploadMap.ms).map((docType) => {
                        if (docType === "ms_bft_report") {
                          return (
                            <React.Fragment key={docType}>
                              {/* Tank Selection Dropdown */}
                              <div className="input-group-dropdown">
                                <Select
                                  id="MSTagNo"
                                  options={tagOptionsMS}
                                  value={selectedTagNoMS}
                                  onChange={handleTagNoChangeMS}
                                  placeholder="Select Tank"
                                  isSearchable
                                  className="tank_dropdown"
                                />
                              </div>

                              {/* Display selected tags as chips */}
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "10px",
                                  marginBottom: "15px",
                                }}
                              >
                                {addedTagsMS.map((tag) => (
                                  <Chip
                                    key={tag.id}
                                    label={tag.tagNo}
                                    onDelete={() => handleRemoveTagMS(tag.id)}
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </div>

                              {/* Dynamic Upload Sections for Each Added Tank */}
                              {addedTagsMS.map((tag) => {
                                const tankIdentifier = tag.tag_no || tag.id;
                                const tankDocType = `ms_bft_report_${tankIdentifier}`;

                                const dynamicConfig = {
                                  key: `msBftReport_${tankIdentifier}`,
                                  label: `MS BFT Report - ${tag.tagNo}`,
                                };

                                return (
                                  <div
                                    key={tankDocType}
                                    className="tank-specific-upload"
                                  >
                                    {renderFileUploadFieldMap(
                                      "ms",
                                      tankDocType,
                                      // msRefs,
                                      msStaticRefs,
                                      dynamicRefs,
                                      uploadStateMS,
                                      setUploadStateMS,
                                      dynamicConfig
                                    )}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        }

                        // Static MS documents
                        return (
                          <React.Fragment key={docType}>
                            {renderFileUploadFieldMap(
                              "ms",
                              docType,
                              // msRefs,
                              msStaticRefs,
                              dynamicRefs,
                              uploadStateMS,
                              setUploadStateMS
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* {selectedFuels.MS && (
                  <div className="product_dflex">
                    <div className="product_label">
                      <label>MS Documents:</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      {Object.keys(documentUploadMap.ms).map((docType) =>
                        renderFileUploadFieldMap(
                          "ms",
                          docType,
                          msRefs,
                          uploadStateMS,
                          setUploadStateMS
                        )
                      )}
                    </div>
                  </div>
                )} */}

                {selectedFuels.HSD && (
                  <div className="product_dflex">
                    <div className="product_label">
                      <label htmlFor="">HSD Documents :</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      {Object.keys(documentUploadMap.hsd).map((docType) => {
                        if (docType === "hsd_bft_report") {
                          return (
                            <React.Fragment key={docType}>
                              {/* Tank Selection Dropdown */}
                              <div className="input-group-dropdown">
                                <Select
                                  id="HSDTagNo"
                                  options={tagOptionsHSD}
                                  value={selectedTagNoHSD}
                                  onChange={handleTagNoChangeHSD}
                                  placeholder="Select Tank"
                                  isSearchable
                                  className="tank_dropdown"
                                />
                              </div>

                              {/* Display selected tags as chips */}
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "10px",
                                  marginBottom: "15px",
                                }}
                              >
                                {addedTagsHSD.map((tag) => (
                                  <Chip
                                    key={tag.id}
                                    label={tag.tagNo}
                                    onDelete={() => handleRemoveTagHSD(tag.id)}
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </div>

                              {/* Dynamic Upload Sections for Each Added Tank */}
                              {addedTagsHSD.map((tag) => {
                                const tankIdentifier = tag.tag_no || tag.id;
                                const tankDocType = `hsd_bft_report_${tankIdentifier}`;

                                const dynamicConfig = {
                                  key: `hsdBftReport_${tankIdentifier}`,
                                  label: `HSD BFT Report - ${tag.tagNo}`,
                                };

                                return (
                                  <div
                                    key={tankDocType}
                                    className="tank-specific-upload"
                                  >
                                    {renderFileUploadFieldMap(
                                      "hsd",
                                      tankDocType,
                                      hsdStaticRefs,
                                      dynamicRefs,
                                      uploadStateHSD,
                                      setUploadStateHSD,
                                      dynamicConfig
                                    )}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        }

                        // Other HSD document types - static
                        return (
                          <React.Fragment key={docType}>
                            {renderFileUploadFieldMap(
                              "hsd",
                              docType,
                              hsdStaticRefs,
                              dynamicRefs,
                              uploadStateHSD,
                              setUploadStateHSD
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* {selectedFuels.HSD && (
                  <div className="product_dflex">
                    <div className="product_label">
                      <label>HSD Documents:</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      {Object.keys(documentUploadMap.hsd).map((docType) =>
                        renderFileUploadFieldMap(
                          "hsd",
                          docType,
                          hsdRefs,
                          uploadStateHSD,
                          setUploadStateHSD
                        )
                      )}
                    </div>
                  </div>
                )} */}

                {/* )} */}

                {/* <div className="form-group ">
                  <label htmlFor="">
                    Products in Voyage<span className="text-danger">*</span>
                  </label>
                  <Select
                    id="product"
                    value={options.find(
                      (opt) => opt.value === voyageData.product
                    )}
                    options={options}
                    onChange={handleVoyageProductChange}
                    placeholder="Select Voyage Product..."
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        backgroundColor: theme === "dark" ? "#111" : "#fff",
                        color: theme === "dark" ? "#fff" : "#333",
                        border: `1px solid ${
                          state.isFocused
                            ? "#007bff"
                            : theme === "dark"
                            ? "#444"
                            : "#ccc"
                        }`,
                        borderRadius: "4px",
                        padding: "2px 4px",
                        boxShadow: "none",
                        minHeight: "36px",
                        fontSize: "14px",
                        cursor: "pointer",
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: theme === "dark" ? "#111" : "#fff",
                        border: `1px solid ${
                          theme === "dark" ? "#444" : "#ccc"
                        }`,
                        borderRadius: "4px",
                        zIndex: 999,
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused
                          ? theme === "dark"
                            ? "#222"
                            : "#f0f0f0"
                          : theme === "dark"
                          ? "#111"
                          : "#fff",
                        color: theme === "dark" ? "#fff" : "#333",
                        fontSize: "14px",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: theme === "dark" ? "#fff" : "#333",
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: theme === "dark" ? "#aaa" : "#888",
                        fontSize: "14px",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "0 8px",
                        color: theme === "dark" ? "#888" : "#666",
                      }),
                    }}
                  />

                  {errors.product && (
                    <div className="error-message">{errors.product}</div>
                  )}
                </div> */}
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000} // Converted to milliseconds
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditVoyage;
